import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

export interface AiCreditWallet {
  free: number;
  paid: number;
  total: number;
  freeDailyQuota: number;
  freeGrantedOn: string | null;
  nextRefillAt: string; // ISO
}

export function useAiCreditWallet(workspaceId: string | undefined) {
  const qc = useQueryClient();
  const q = useQuery<AiCreditWallet>({
    queryKey: ["ai-credits-wallet", workspaceId],
    queryFn: async () => {
      if (!workspaceId) {
        return { free: 0, paid: 0, total: 0, freeDailyQuota: 5, freeGrantedOn: null, nextRefillAt: new Date().toISOString() };
      }
      const { data, error } = await supabase.rpc("get_ai_credit_wallet", { _workspace_id: workspaceId });
      if (error) throw error;
      const d: any = data || {};
      return {
        free: Number(d.free_balance ?? 0),
        paid: Number(d.paid_balance ?? 0),
        total: Number(d.total ?? 0),
        freeDailyQuota: Number(d.free_daily_quota ?? 5),
        freeGrantedOn: d.free_granted_on ?? null,
        nextRefillAt: d.next_refill_at ?? new Date().toISOString(),
      };
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!workspaceId) return;
    const ch = supabase
      .channel(`ai-credits-${workspaceId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "ai_credit_wallets", filter: `workspace_id=eq.${workspaceId}` }, () => {
        qc.invalidateQueries({ queryKey: ["ai-credits-wallet", workspaceId] });
        qc.invalidateQueries({ queryKey: ["ai-credits-balance", workspaceId] });
        qc.invalidateQueries({ queryKey: ["ai-credits-transactions", workspaceId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [workspaceId, qc]);

  return q;
}

export function useAiCreditBalance(workspaceId: string | undefined) {
  const w = useAiCreditWallet(workspaceId);
  return { ...w, data: w.data?.total ?? 0 } as any;
}

export function useAiCreditTransactions(workspaceId: string | undefined, limit = 10) {
  return useQuery({
    queryKey: ["ai-credits-transactions", workspaceId, limit],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("ai_credit_transactions")
        .select("id, delta, reason, reference_id, created_at")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data || [];
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

export async function consumeAiCredit(workspaceId: string, reason: string, ref?: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("consume_ai_credit", {
    _workspace_id: workspaceId,
    _reason: reason,
    _ref: ref ?? null,
  });
  if (error) throw error;
  return data === true;
}

export function formatRefillCountdown(nextRefillAt: string): string {
  const ms = new Date(nextRefillAt).getTime() - Date.now();
  if (ms <= 0) return "soon";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}
